// Third-party
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router';
import camelCase from 'lodash/camelCase';
import { LocationDescriptorObject } from 'history';

// Components
import { IntegerValue } from '../../../components/common';
import { IconButton, PrimaryButton } from '../../../components/buttons';
import SavedItemsTable from './SavedItemsTable';
import { Tooltip } from '../../../components/tooltips';

// Actions
import { ReduxState } from '../../../../redux/modules/types';
import { getSavedBenchmarks, toggleSavedBenchmark } from '../../../../redux/modules/comparison-benchmarks/saved-benchmarks';
import { addBenchmarksToComparison } from '../../../../redux/modules/comparison';
import { addBenchmarksToPortfolio, loadNewBenchmark } from '../../../../redux/modules/edit-benchmark';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../../utils/3rd-party/heap';
import { formatMessage, formatNumber, formatLocalizedString, FormattedMessage } from '../../../../utils/i18n';
import { searchObjectsOnFields } from '../../../../utils/search';
import { NAV_PATHS } from '../../../../utils/constants';
import ComparisonBenchmark from '@/types/comparison-benchmark';

const roundValue =
  (digits: number) =>
  ({ value }: any) => <span>{value ? formatNumber(value, digits) : '-'}</span>;

type DispatchProps = {
  getSavedBenchmarks: () => Promise<ComparisonBenchmark[]>;
  toggleSavedBenchmark: (benchmark: ComparisonBenchmark) => Promise<ComparisonBenchmark[]>;
  addBenchmarksToComparison: (benchmarks: ComparisonBenchmark[]) => Promise<void>;
  addBenchmarksToPortfolio: (benchmarks: ComparisonBenchmark[]) => Promise<void[]>;
  loadNewBenchmark: () => void;
};

type StateProps = {
  benchmarks: ComparisonBenchmark[];
  loading: boolean;
};

type Props = RouteComponentProps &
  DispatchProps &
  StateProps & {
    query?: string;
  };

interface State {
  selected: ComparisonBenchmark[];
  redirect?: string | LocationDescriptorObject;
}

class SavedBenchmarks extends React.Component<Props, State> {
  readonly state: State = {
    selected: [],
  };

  readonly columns: any[] = [
    {
      Header: () => <span>{formatMessage('Benchmarks.Table.Headers.BenchmarkName')}</span>,
      accessor: 'name',
      className: 'benchmark-name',
      minWidth: 275,
      Cell: ({ original, value }: { original: ComparisonBenchmark; value: string }) => (
        <Tooltip message={value} delayShow={300}>
          <span>{value}</span>
        </Tooltip>
      ),
    },
    {
      Header: () => <span>{formatMessage('Benchmarks.Table.Headers.Classification')}</span>,
      accessor: 'classification',
      minWidth: 140,
    },
    {
      Header: () => <span>{formatMessage('Benchmarks.Table.Headers.3Y')}</span>,
      accessor: 'performance.percentGrowth.3Y',
      Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
      width: 90,
    },
    {
      Header: () => <span>{formatMessage('Benchmarks.Table.Headers.5Y')}</span>,
      accessor: 'performance.percentGrowth.5Y',
      Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
      width: 90,
    },
    {
      Header: () => <span>{formatMessage('Benchmarks.Table.Headers.10Y')}</span>,
      accessor: 'performance.percentGrowth.10Y',
      Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
      width: 90,
    },
    {
      Header: () => <span>{formatMessage('Benchmarks.Table.Headers.YT')}</span>,
      accessor: 'performance.percentGrowth.YT',
      Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
      width: 120,
    },
    {
      Header: () => <span>{formatMessage('Benchmarks.Table.Headers.StdDev')}</span>,
      accessor: 'performance.standardDeviation.3Y',
      Cell: roundValue(2),
      width: 120,
    },
    {
      accessor: '_id',
      className: 'cell-icon',
      Cell: ({ original }: { original: ComparisonBenchmark }) => <IconButton icon="ds-delete" onClick={() => this.toggleSaved(original)} />,
      width: 48,
    },
  ];

  onAddToPortfolio = () => {
    this.props.loadNewBenchmark();
    this.props.addBenchmarksToPortfolio(this.state.selected).then(() => {
      this.setState({ redirect: '/models/custom-benchmarks?newBenchmarkPortfolio=true' });
    });
  };

  onAddToComparison = () => {
    const { location } = this.props;
    this.props.addBenchmarksToComparison(this.state.selected).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({
        redirect: {
          pathname: NAV_PATHS.COMPARISON,
          state: { from: location },
        },
      });
    });
  };

  getActions = () => [
    <PrimaryButton key="action-add" compact icon="plus" onClick={this.onAddToPortfolio}>
      <FormattedMessage id="Search.Benchmarks.CreateCustom" />
    </PrimaryButton>,
    <PrimaryButton key="action-compare" compact icon="plus" onClick={this.onAddToComparison}>
      <FormattedMessage id="Search.Benchmarks.AddToComparison" />
    </PrimaryButton>,
  ];

  onSelectSavedBenchmarks = (ids: string[]) => {
    const { benchmarks } = this.props;

    const selected = benchmarks.filter(b => ids.includes(b._id));

    this.setState(prevState => ({
      selected: [...prevState.selected, ...selected],
    }));
  };

  onUnselectSavedBenchmarks = (ids: string[]) => {
    this.setState(prevState => ({
      selected: prevState.selected.filter(b => !ids.includes(b._id)),
    }));
  };

  getFilteredBenchmarks = () => {
    const { query, benchmarks } = this.props;

    const data = this.formatData(benchmarks);

    if (!query) {
      return data;
    }

    return searchObjectsOnFields(query, data, ['name', 'ticker', 'classification']);
  };

  toggleSaved = (row: ComparisonBenchmark) => {
    const { benchmarks } = this.props;
    const benchmark = benchmarks.find(f => f._id === row._id);
    if (!benchmark) {
      return;
    }

    this.props.toggleSavedBenchmark(benchmark);

    this.setState(prevState => ({
      selected: prevState.selected.filter(f => f !== row),
    }));
  };

  formatData = (benchmarks: ComparisonBenchmark[]) =>
    benchmarks.map(benchmark => ({
      ...benchmark,
      name: formatLocalizedString(benchmark.name),
      classification: formatLocalizedString(benchmark.classification),
    }));

  redirectToSearch = () => this.setState({ redirect: '/search/benchmarks' });

  render() {
    const { loading, query } = this.props;
    const { selected, redirect } = this.state;

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    return (
      <SavedItemsTable
        className="saved-benchmarks"
        data={this.getFilteredBenchmarks()}
        columns={this.columns}
        loading={loading}
        selected={selected}
        query={query}
        actions={this.getActions()}
        onSelectItems={this.onSelectSavedBenchmarks}
        onUnselectItems={this.onUnselectSavedBenchmarks}
        emptyResultsAction={this.redirectToSearch}
        emptyResultsActionLabel={formatMessage('Search.SavedBenchmarks.NoData.GetStarted')}
        emptyResultsActionIcon="icon-arrow-up-right2"
        noDataTitle={formatMessage('Search.SavedBenchmarks.NoData.Title')}
        noDataBodyText={formatMessage('Search.SavedBenchmarks.NoData.Text')}
        noResultsTitle={formatMessage('Search.NoSearchResults.Title')}
        noResultsBodyText={formatMessage('Search.NoSearchResults.Text')}
        selectedTitle={formatMessage('Search.Benchmarks.SelectedTitle', { count: selected.length })}
      />
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  benchmarks: state.comparisonBenchmarks.savedBenchmarks.benchmarks,
  loading: state.comparisonBenchmarks.savedBenchmarks.loading,
});

export default connect(mapStateToProps, {
  getSavedBenchmarks,
  toggleSavedBenchmark,
  addBenchmarksToComparison,
  addBenchmarksToPortfolio,
  loadNewBenchmark,
})(SavedBenchmarks);
